import { assistantResponseSchema, type AssistantRequest, type AssistantResponse } from "@otrack/commands/assistant";
import { GoogleRequestError, type GoogleApi } from "@otrack/app-shell";
import {
  apiErrorSchema, googleApplyResponseSchema, googleCalendarsResponseSchema, googleConnectResponseSchema, googleEventsResponseSchema, googleStatusSchema, pullResponseSchema, pushResponseSchema,
  type GoogleConnectRequest, type PullResponse, type PushRequest, type PushResponse
} from "@otrack/domain";
import { SyncRequestError, type SyncApi } from "@otrack/local-store";

/** Returns a session token, or null when requests go without one (local development). */
export type TokenProvider = () => Promise<string | null>;

export interface OTrackApi extends SyncApi {
  assistant(request: AssistantRequest): Promise<AssistantResponse>;
  /** Google Calendar through the Worker, with the signed-in User's own Google access. */
  google: GoogleApi;
  /** Google's consent page for calendar access, run by the Worker apart from sign-in (ADR 0029). */
  googleConsent(returnTo: GoogleConnectRequest["returnTo"]): Promise<string>;
}

export function createApi(origin: string, getToken: TokenProvider): OTrackApi {
  const request = async (path: string, init?: RequestInit): Promise<unknown> => {
    const token = await getToken();
    let response: Response;
    try {
      response = await fetch(`${origin}${path}`, { ...init, headers: { "content-type": "application/json", ...(token ? { authorization: `Bearer ${token}` } : {}), ...init?.headers } });
    } catch {
      throw new SyncRequestError("ontrack's server is unreachable", null, true);
    }
    const body: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      const parsed = apiErrorSchema.safeParse(body);
      throw new SyncRequestError(parsed.success ? parsed.data.error.message : `Request failed (${response.status})`, response.status, response.status >= 500 || response.status === 429);
    }
    return body;
  };
  /** Google requests keep the Worker's error code, which says whether to reconnect or retry. */
  const googleRequest = async (path: string, init?: RequestInit): Promise<unknown> => {
    const token = await getToken();
    let response: Response;
    try {
      response = await fetch(`${origin}${path}`, { ...init, headers: { "content-type": "application/json", ...(token ? { authorization: `Bearer ${token}` } : {}) } });
    } catch {
      throw new GoogleRequestError("ontrack's server is unreachable", null);
    }
    const body: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      const parsed = apiErrorSchema.safeParse(body);
      throw new GoogleRequestError(parsed.success ? parsed.data.error.message : `Google Calendar failed (${response.status})`, parsed.success ? parsed.data.error.code : null);
    }
    return body;
  };
  return {
    async push(body: PushRequest): Promise<PushResponse> {
      return pushResponseSchema.parse(await request("/api/sync/push", { method: "POST", body: JSON.stringify(body) }));
    },
    async pull(after: number): Promise<PullResponse> {
      return pullResponseSchema.parse(await request(`/api/sync/pull?after=${after}`));
    },
    async assistant(body: AssistantRequest): Promise<AssistantResponse> {
      return assistantResponseSchema.parse(await request("/api/assistant", { method: "POST", body: JSON.stringify(body) }));
    },
    google: {
      async status() {
        return googleStatusSchema.parse(await googleRequest("/api/google/status"));
      },
      async calendars() {
        return googleCalendarsResponseSchema.parse(await googleRequest("/api/google/calendars")).calendars;
      },
      async events(calendarId, syncToken) {
        const query = new URLSearchParams({ calendarId, ...(syncToken ? { syncToken } : {}) });
        return googleEventsResponseSchema.parse(await googleRequest(`/api/google/events?${query.toString()}`));
      },
      async apply(changes) {
        return googleApplyResponseSchema.parse(await googleRequest("/api/google/apply", { method: "POST", body: JSON.stringify({ changes }) })).results;
      },
      async disconnect() {
        await googleRequest("/api/google/disconnect", { method: "POST", body: "{}" });
      }
    },
    async googleConsent(returnTo) {
      return googleConnectResponseSchema.parse(await googleRequest("/api/google/connect", { method: "POST", body: JSON.stringify({ returnTo }) })).url;
    }
  };
}
